'use client';
import { ChangeEvent, useState } from 'react';
import cn from 'classnames';
import { Tooltip } from 'react-tooltip';
import Image from 'next/image';

import './SignUpSummaryEditMobile.style.scss';
import LoadingSpinner from '../LoadingSpinner/LoadingSpinner';
import CircleQuestionMark from 'public/img/icon/circle-questionmark.svg';
import Close from 'public/img/icon/close.svg';

import AnimatedAccordion from '@/components/common/AnimatedAccordion/AnimatedAccordion';
import RadioGroup from '@/components/common/RadioGroup/RadioGroup';
import SignUpPlanCardList from '@/components/common/SignUpPlanCardList/SignUpPlanCardList';
import Text from '@/components/common/Text/Text';
import { ChangeValueAction } from '@/components/modal/SignUpSummaryModal/SignUpSummaryModal';

import { RawEPCCCartResponse } from '@/types/Cart';
import {
  AddOnType,
  InsuranceType,
  SubscriptionProduct
} from '@/types/Subscription';

type SignUpSummaryEditMobileProps = {
  className?: string;
  addOnValue: AddOnType;
  referralValue?: string;
  referralCopy?: string;
  insuranceValue: InsuranceType;
  onValueChange: (changeValue: ChangeValueAction) => void;
  selectedPlanValue: SubscriptionProduct;
  subs: SubscriptionProduct[];
  previewCart?: RawEPCCCartResponse;
};

type SummaryAccordionState = {
  addOns: boolean;
  coverage: boolean;
  membershipTier: boolean;
  referral: boolean;
};

const ALL_ACCORDIONS_CLOSED: SummaryAccordionState = {
  addOns: false,
  coverage: false,
  membershipTier: false,
  referral: false
};

function SignUpSummaryEditMobile(props: SignUpSummaryEditMobileProps) {
  const {
    className = '',
    subs,
    addOnValue,
    insuranceValue,
    referralValue,
    referralCopy,
    selectedPlanValue,
    onValueChange,
    previewCart
  } = props;

  const [accordionState, setAccordionState] = useState<SummaryAccordionState>(
    ALL_ACCORDIONS_CLOSED
  );

  const handleAccordionAction = (
    accordionName: keyof SummaryAccordionState
  ) => {
    const isCurrentlyOpenedAccordion = accordionState[accordionName] === true;

    if (isCurrentlyOpenedAccordion) {
      setAccordionState({
        addOns: false,
        coverage: false,
        membershipTier: false,
        referral: false
      });
      return;
    }

    setAccordionState({
      ...ALL_ACCORDIONS_CLOSED,
      [accordionName]: true
    });
  };

  const isPremierPlanSelected = selectedPlanValue.slug === 'premier';

  return (
    <div className={cn('SignUpSummaryEditMobile', className)}>
      <div className='SignUpSummaryEditMobile-PlanChoiceOptions SignUpSummaryEditMobile-PlanChoiceOptions--plans'>
        <AnimatedAccordion
          open={accordionState.membershipTier}
          title={
            <Text
              uppercase
              variant='title-xxs-500'
            >
              Membership tier
            </Text>
          }
          expandedHeight={455}
          onRequestChange={() => {
            handleAccordionAction('membershipTier');
          }}
        >
          <SignUpPlanCardList
            subs={subs}
            selectedPlanValue={selectedPlanValue}
            onPlanSelected={(selectedPlanValue: SubscriptionProduct) => {
              onValueChange({
                type: 'subscription_plan',
                value: selectedPlanValue as SubscriptionProduct
              });
            }}
            isPremierAddOnSelected={addOnValue === 'premier'}
          />
        </AnimatedAccordion>
      </div>

      <div className='SignUpSummaryEditMobile-PlanChoiceOptions'>
        <AnimatedAccordion
          open={accordionState.addOns}
          title={
            <Text
              uppercase
              variant='title-xxs-500'
            >
              Add-ons
            </Text>
          }
          expandedHeight={210}
          onRequestChange={() => {
            handleAccordionAction('addOns');
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '2px' }}>
            <Text>
              Add Premier to your membership to experience an additional piece
              from our Premier closet.
            </Text>
            <button
              className='SignUpSummaryEditMobile-premier-info-anchor cursor'
              style={{
                background: 'none',
                border: 'none',
                padding: 0,
                cursor: 'pointer'
              }}
            >
              <Image
                src={CircleQuestionMark}
                alt='Info'
                width={14}
                height={14}
              />
            </button>
          </div>

          <Tooltip
            anchorSelect='.SignUpSummaryEditMobile-premier-info-anchor'
            className='SignUpSummaryBar-tooltip cursor'
            classNameArrow='SignUpSummaryBar-tooltip-arrow'
            place='top'
            opacity={1}
          >
            <div className='SignUpSummaryBar-tooltip-content'>
              <Text variant='title-xxs-500-upper'>
                What&apos;s included in the premier closet?
              </Text>
              <Text variant='text-s'>
                The Premier closet includes jewelry up to $1,000 and select
                designer bags.
              </Text>
            </div>
          </Tooltip>

          <RadioGroup
            value={addOnValue}
            onChange={(event: ChangeEvent<HTMLInputElement>): void => {
              const { value } = event.currentTarget;
              onValueChange({
                type: 'addon',
                value: value as AddOnType
              });
            }}
            name='radiogroup-addon-mobile'
            options={[
              {
                label: `Premier ${
                  (
                    subs.find((sub) => sub.slug === 'premier') ??
                    subs.sort((a, b) => a.price.asNumber - b.price.asNumber)[0]
                  ).price.asFormattedString.split('.')[0]
                }/month`,
                value: 'premier',
                disabled: isPremierPlanSelected
              },
              {
                label: 'None',
                value: 'none'
              }
            ]}
          />
        </AnimatedAccordion>
      </div>

      <div className='SignUpSummaryEditMobile-PlanChoiceOptions'>
        <AnimatedAccordion
          open={accordionState.coverage}
          title={
            <Text
              uppercase
              variant='title-xxs-500'
            >
              Coverage
            </Text>
          }
          expandedHeight={210}
          onRequestChange={() => {
            handleAccordionAction('coverage');
          }}
        >
          <Text>
            Included coverage protects against normal wear & tear. Upgrade to
            premium coverage for accidental damage protection.
          </Text>
          <RadioGroup
            value={insuranceValue}
            onChange={(event: ChangeEvent<HTMLInputElement>): void => {
              const { value } = event.currentTarget;
              onValueChange({
                type: 'insurance',
                value: value as InsuranceType
              });
            }}
            name='radiogroup-insurance-mobile'
            // @TODO make this data dynamic, see <UpdateMembershipForm />
            options={[
              { label: 'Premium $18/month', value: 'premium' },
              { label: 'Standard', value: 'standard' }
            ]}
          />
        </AnimatedAccordion>
      </div>

      <div className='SignUpSummaryEditMobile-PlanChoiceOptions'>
        <AnimatedAccordion
          open={accordionState.referral}
          title={
            <Text
              uppercase
              variant='title-xxs-500'
            >
              Referral code/name
            </Text>
          }
          expandedHeight={210}
          onRequestChange={() => {
            handleAccordionAction('referral');
          }}
        >
          {!!referralCopy && <Text>{referralCopy}</Text>}

          <input
            type='text'
            className='SignUpSummaryEditMobile-referral-code'
            name='referralCode'
            placeholder='None'
            value={referralValue}
            onChange={(event: ChangeEvent<HTMLInputElement>): void => {
              const { value } = event.currentTarget;
              onValueChange({
                type: 'referral_code',
                value: value
              });
            }}
          />
        </AnimatedAccordion>
        <div className='PlanChoiceBottom-title'></div>
      </div>

      <div className='SignUpSummaryEditMobile-PlanChoiceOptions SignUpSummaryEditMobile-PlanChoiceOptions--billing'>
        <Text
          uppercase={true}
          variant={'title-xxs-500'}
        >
          your Billing cycle
        </Text>
        <Text>
          Your billing cycle does not start until your membership application
          has been reviewed by our team and accepted.
        </Text>
        <div className='PlanChoiceBillingCycle-value-monthly'>
          <span className='PlanChoiceBilling-label'>Monthly: </span>
          {!previewCart ? (
            <LoadingSpinner size={10} />
          ) : (
            <>{previewCart?.data?.meta.display_price.without_tax.formatted}</>
          )}
        </div>
        <div className='PlanChoiceBillingCycle-value-due-now'>
          <span className='PlanChoiceBilling-label'>Due Now: </span>
          <span className='PlanChoiceBilling-value'>$0</span>

          <button className='SignUpSummaryBar-tooltip-anchor cursor'>
            <Image
              src={CircleQuestionMark}
              alt='Info'
              width={14}
              height={14}
              style={{ marginTop: '-3px', marginLeft: '3px' }}
            />
          </button>
        </div>

        <Tooltip
          anchorSelect='.SignUpSummaryBar-tooltip-anchor'
          className='SignUpSummaryBar-tooltip cursor'
          classNameArrow='SignUpSummaryBar-tooltip-arrow'
          place='right'
        >
          <div className='SignUpSummaryBar-tooltip-content'>
            <Image
              className='tooltip-close'
              src={Close}
              alt=''
            />
            <Text
              variant='title-xxs-500'
              uppercase
            >
              When will I be charged?
            </Text>
            <Text variant={'text-s'}>
              Your billing cycle will begin once your membership application has
              been reviewed by our team and accepted.
            </Text>
          </div>
        </Tooltip>
      </div>
    </div>
  );
}

export default SignUpSummaryEditMobile;
